import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageCircle,
  ShoppingBag,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type Props = {
  participantId: string | null;
  onClose: () => void;
};

export default function MessengerUserProfile({ participantId, onClose }: Props) {
  const open = Boolean(participantId);
  const [showTech, setShowTech] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["messenger-participant", participantId],
    queryFn: () => api.messengerParticipantProfile(participantId!),
    enabled: open,
  });

  if (!open) return null;

  const shortPsid =
    participantId && participantId.length > 12
      ? `${participantId.slice(0, 6)}…${participantId.slice(-4)}`
      : participantId;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col",
          "border-l border-slate-200 bg-white shadow-2xl",
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5">
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold text-slate-900">
              {data?.participant_name || "Profil osoby"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Pełna historia: rozmowy, komentarze i zakupy
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {data?.inbox_link ? (
              <a href={data.inbox_link} target="_blank" rel="noreferrer">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-200 text-slate-700"
                >
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  Inbox Meta
                </Button>
              </a>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Zamknij"
              className="text-slate-500"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <p className="text-sm text-slate-500">Wczytywanie profilu…</p>
          )}
          {isError && (
            <p className="text-sm text-red-600">Nie udało się wczytać profilu.</p>
          )}
          {data && (
            <>
              <button
                type="button"
                className="mb-4 flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs text-slate-600"
                onClick={() => setShowTech(!showTech)}
              >
                <span>Szczegóły techniczne (PSID, e-mail)</span>
                {showTech ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {showTech && (
                <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                  <p>PSID: {shortPsid}</p>
                  {data.emails?.length ? (
                    <p>E-mail z DM: {data.emails.join(", ")}</p>
                  ) : (
                    <p>Brak e-mailu w wiadomościach</p>
                  )}
                </div>
              )}

              <Tabs defaultValue="messenger">
                <TabsList className="w-full bg-slate-100">
                  <TabsTrigger value="messenger" className="flex-1 gap-1.5 text-xs sm:text-sm">
                    <MessageCircle className="h-3.5 w-3.5" />
                    Messenger ({data.messenger_threads?.length ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="flex-1 gap-1.5 text-xs sm:text-sm">
                    <MessageCircle className="h-3.5 w-3.5" />
                    Komentarze ({data.comments?.length ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value="orders" className="flex-1 gap-1.5 text-xs sm:text-sm">
                    <ShoppingBag className="h-3.5 w-3.5" />
                    Zakupy ({data.orders?.length ?? 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="messenger" className="mt-4 space-y-4">
                  {(data.messenger_threads ?? []).map((thread) => (
                    <div
                      key={thread.thread_id}
                      className="rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                        <span>{thread.message_count} wiadomości</span>
                        <span>{thread.updated_at?.slice(0, 10)}</span>
                      </div>
                      <div className="max-h-80 space-y-2 overflow-y-auto">
                        {(thread.messages ?? []).map((msg) => (
                          <div
                            key={msg.id}
                            className={cn(
                              "rounded-lg px-3 py-2 text-sm",
                              msg.sender === "user"
                                ? "bg-pink-50 text-slate-800 border border-pink-100"
                                : "bg-slate-100 text-slate-600",
                            )}
                          >
                            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              {msg.sender === "user" ? msg.from_name || "Osoba" : "Fanpage"}
                              {msg.created_at
                                ? ` · ${msg.created_at.slice(0, 16).replace("T", " ")}`
                                : ""}
                            </div>
                            <div className="whitespace-pre-wrap">
                              {msg.text || "_(pusta wiadomość lub załącznik)_"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!data.messenger_threads?.length && (
                    <p className="text-sm text-slate-500">Brak wątków do wyświetlenia.</p>
                  )}
                </TabsContent>

                <TabsContent value="comments" className="mt-4 space-y-3">
                  {(data.comments ?? []).map((c) => (
                    <div key={c.id} className="rounded-xl border border-slate-200 p-3">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="border-slate-200 text-slate-600">
                          {c.platform}
                        </Badge>
                        <Badge
                          variant={c.match_confidence === "confirmed" ? "default" : "secondary"}
                        >
                          {c.match_confidence === "confirmed" ? "Pewne" : "Prawdopodobne"}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {c.created_at?.slice(0, 10)}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-slate-700">{c.author}</p>
                      <p className="mt-1 text-sm text-slate-600">{c.text}</p>
                    </div>
                  ))}
                  {!data.comments?.length && (
                    <p className="text-sm text-slate-500">
                      Brak dopasowanych komentarów pod tym imieniem lub e-mailem.
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="orders" className="mt-4 space-y-3">
                  {(data.orders ?? []).map((o) => (
                    <div key={o.order_ref} className="rounded-xl border border-slate-200 p-3">
                      <div className="font-medium text-slate-900">
                        {o.product_name || o.order_ref}
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        {o.revenue_amount > 0 ? `${o.revenue_amount} ${o.currency}` : "—"} ·{" "}
                        {o.ordered_at?.slice(0, 10) || "—"}
                      </p>
                      {o.fulfillment_status || o.fulfillment_label ? (
                        <p className="text-xs text-slate-500">
                          Wysyłka: {o.fulfillment_status || o.fulfillment_label}
                        </p>
                      ) : null}
                    </div>
                  ))}
                  {!data.orders?.length && (
                    <p className="text-sm text-slate-500">{data.journey_note}</p>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
